// My books (Phase 6.4, Part 1, agreed with Kristi Oct 31; mocked up in chat): the section's
// tabs and their addresses. No database calls here, so the rules are tested on their own
// (tests/logic/my-books.test.ts).

export type MyBooksTab = 'library' | 'owned' | 'add' | 'review';

export interface MyBooksTabDef {
  key: MyBooksTab;
  name: string;
  /** Its name on a phone (consistency pass R9 M1, Kristi Nov 2). */
  short: string;
  /** The tab's own address under /my-books. */
  path: string;
  description: string;
}

export const MY_BOOKS = '/my-books';

/**
 * Library copies · Owned copies · Add & import · Update details (filters review, Kristi Nov 2):
 * Recently added is the last section of Add & import now, and Review is Update details.
 */
export const MY_BOOKS_TABS: MyBooksTabDef[] = [
  {
    key: 'library',
    name: 'Library copies',
    short: 'Library',
    path: `${MY_BOOKS}/library`,
    description: 'Check and update your library holds and loans.',
  },
  {
    key: 'owned',
    name: 'Owned copies',
    short: 'Owned',
    path: `${MY_BOOKS}/owned`,
    description: 'Review and update book details for books you own, and specify a physical location for paper books.',
  },
  {
    key: 'add',
    name: 'Add & import',
    short: 'Add',
    path: `${MY_BOOKS}/add`,
    description: 'Add books one at a time or as a list, and look over what you’ve added.',
  },
  {
    // Was Review (Kristi, Nov 2): No shelf yet is its first list. The key stays "review", so this
    // device's choices (the list you were on) come along.
    key: 'review',
    name: 'Update details',
    short: 'Details',
    path: `${MY_BOOKS}/details`,
    description: 'Fill in what’s missing and check suggested fixes, so books show, sort and filter well.',
  },
];

export const myBooksTab = (k: MyBooksTab) => MY_BOOKS_TABS.find((t) => t.key === k)!;

/** Addresses from before (Oct 31, Nov 2), still opening where they went, so old links work. */
export const OLD_REVIEW_PATHS = [`${MY_BOOKS}/recently-added/review`, `${MY_BOOKS}/review`];
export const OLD_RECENT_PATH = `${MY_BOOKS}/recently-added`;

/**
 * What an address under /my-books shows: its tab, and for Recently added's old address, the section
 * of Add & import it went to. `/my-books` alone (or an address it doesn't know) has no tab of its
 * own: the page opens the tab you last used, Library copies at first.
 */
export function myBooksRoute(path: string): { tab: MyBooksTab | null; section?: 'recent' } {
  const p = path.replace(/\/+$/, '');
  if (OLD_REVIEW_PATHS.includes(p)) return { tab: 'review' };
  if (p === OLD_RECENT_PATH) return { tab: 'add', section: 'recent' };
  const t = MY_BOOKS_TABS.find((d) => d.path === p);
  return { tab: t ? t.key : null };
}

/** The tab a plain /my-books opens: the one last used on this device, else Library copies (the first). */
export function startTab(remembered: unknown): MyBooksTab {
  if (remembered === 'recently_added') return 'add';
  return MY_BOOKS_TABS.some((t) => t.key === remembered) ? (remembered as MyBooksTab) : 'library';
}
